import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useThemeColours } from '@/shared/lib/theme/use-theme-colours';
import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * A quantity over time.
 *
 * The Apex configuration is TailAdmin's (MIT — `shared/tailadmin/LICENSE.md`): no toolbar, no data
 * labels, horizontal grid lines only, a gradient that fades to nothing. What arrived with it was a
 * component called `LineChartOne` holding twelve months of invented sales figures and two hard-coded
 * indigo hexes.
 *
 * ⚠️ **Rewritten to take its data as props rather than to contain it.** A chart that carries its own
 * numbers is a picture; sprint 6 needs charts, and every one of them draws something the API knows.
 *
 * ⚠️ **The colours are read from the active theme, not written here.** Apex draws SVG with inline
 * attributes and never consults the cascade, so `var(--color-primary)` reaches it as an invalid
 * colour and the series comes out black. This is the same reason `useThemeColours` exists for the
 * map, and it is the only sanctioned way to put a colour into JavaScript in this panel (§7.2).
 *
 * ⚠️ **`rtl` is set from the locale.** Apex lays out its own axes and legend, and an Arabic page
 * with a chart that reads left-to-right puts the earliest month under the reader's last glance.
 */
const TOKENS = ['primary', 'secondary', 'info'] as const;

export function AreaChart({
  series,
  categories,
  height = 310,
  formatValue,
}: {
  series: readonly { name: string; data: readonly number[] }[];
  categories: readonly string[];
  height?: number;

  /** How a value reads in a tooltip — a count, a distance, a duration. */
  formatValue?: (value: number) => string;
}) {
  const colours = useThemeColours(TOKENS);
  const { locale } = useLocale();

  const options: ApexOptions = {
    colors: [colours.primary, colours.secondary, colours.info],
    chart: {
      fontFamily: 'inherit',
      height,
      type: 'area',
      toolbar: { show: false },
      // ⚠️ Apex animates on every re-render, and the panel re-renders on every live event. On a
      // screen watched continuously that is a chart which is always mid-animation.
      animations: { enabled: false },
    },
    legend: { show: series.length > 1, position: 'top', horizontalAlign: 'left' },
    stroke: { curve: 'straight', width: 2 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.55, opacityTo: 0 } },
    markers: { size: 0, strokeWidth: 2, hover: { size: 6 } },
    dataLabels: { enabled: false },
    grid: { xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    tooltip: {
      enabled: true,
      y: formatValue ? { formatter: formatValue } : undefined,
    },
    xaxis: {
      type: 'category',
      categories: [...categories],
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    /*
     * ⚠️ **The axis is formatted too, not only the tooltip.**
     *
     * Apex numbers its own axis with Latin digits whatever the page's locale is, and this panel
     * writes its figures in Arabic-Indic ones. Left alone, a single card carries ٦ in its list and 6
     * on the scale beside it — the same number in two alphabets, which reads as two numbers.
     * Observed on the statistics screen against live data before it was fixed.
     */
    yaxis: {
      labels: {
        style: { fontSize: '12px' },
        ...(formatValue ? { formatter: formatValue } : {}),
      },
      title: { text: '', style: { fontSize: '0px' } },
    },
  };

  return (
    // ⚠️ Scrolls inside its own box. A chart wider than the card is a card that scrolls the page
    // sideways, and on a control-room monitor that moves every other panel with it.
    <div className="custom-scrollbar max-w-full overflow-x-auto" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="min-w-[640px] xl:min-w-full">
        <Chart options={options} series={series as ApexOptions['series']} type="area" height={height} />
      </div>
    </div>
  );
}
